/**
 * AccessibilityPanel — Composing panel for all end-user a11y settings.
 *
 * Includes typography (font size, family, line spacing), visual
 * (high contrast, CVD mode), and motion (animation toggle) sections.
 *
 * @module components/accessibility/AccessibilityPanel
 */
import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Trans, useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import {
  useAccessibilitySettings,
  type AccessibilitySettings,
  type CvdMode,
  type LineSpacing,
} from '../../hooks/useAccessibilitySettings';
import { FontSizeControl } from './FontSizeControl';
import { FontFamilyControl } from './FontFamilyControl';
import { AnimationControl } from './AnimationControl';
import { HighContrastToggle } from './HighContrastToggle';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AccessibilityPanelProps {
  /** Callback fired on every setting change with the full current settings. */
  onPreferencesChange?: (prefs: AccessibilitySettings) => void;
  /** Override the panel title. */
  title?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LINE_SPACING_OPTIONS: LineSpacing[] = [1.2, 1.5, 1.8, 2.0];

const CVD_MODE_OPTIONS: Array<{ value: CvdMode; label: ReturnType<typeof msg> }> = [
  { value: 'none', label: msg`None` },
  { value: 'protanopia', label: msg`Protanopia (Red-blind)` },
  { value: 'deuteranopia', label: msg`Deuteranopia (Green-blind)` },
  { value: 'tritanopia', label: msg`Tritanopia (Blue-blind)` },
  { value: 'achromatopsia', label: msg`Achromatopsia (Monochrome)` },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AccessibilityPanel: React.FC<AccessibilityPanelProps> = ({
  onPreferencesChange,
  title,
}) => {
  const { t } = useLingui();
  const settings = useAccessibilitySettings();
  const { lineSpacing, cvdMode, updateSettings, resetSettings } = settings;

  // Fire callback on every settings change
  const callbackRef = useRef(onPreferencesChange);
  callbackRef.current = onPreferencesChange;

  useEffect(() => {
    callbackRef.current?.({
      theme: settings.theme,
      fontScale: settings.fontScale,
      animationsEnabled: settings.animationsEnabled,
      highContrast: settings.highContrast,
      lineSpacing: settings.lineSpacing,
      cvdMode: settings.cvdMode,
      fontFamily: settings.fontFamily,
      fontSize: settings.fontSize,
    });
  }, [
    settings.theme,
    settings.fontScale,
    settings.animationsEnabled,
    settings.highContrast,
    settings.lineSpacing,
    settings.cvdMode,
    settings.fontFamily,
    settings.fontSize,
  ]);

  const handleLineSpacingChange = (
    _: React.MouseEvent<HTMLElement>,
    value: LineSpacing | null,
  ) => {
    if (value !== null) {
      updateSettings({ lineSpacing: value });
    }
  };

  const handleCvdModeChange = (e: SelectChangeEvent) => {
    updateSettings({ cvdMode: e.target.value as CvdMode });
  };

  return (
    <Paper
      sx={{ p: 3, maxWidth: 480 }}
      role="region"
      aria-label={title ?? t`Accessibility Settings`}
    >
      {/* Title */}
      <Typography variant="h6" gutterBottom>
        {title ?? <Trans>Accessibility Settings</Trans>}
      </Typography>

      {/* ---- Typography Section ---- */}
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        <Trans>Typography</Trans>
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FontSizeControl />
      </Box>

      <Box sx={{ mb: 3 }}>
        <FontFamilyControl />
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom id="a11y-line-spacing-label">
          <Trans>Line Spacing</Trans>
        </Typography>
        <ToggleButtonGroup
          value={lineSpacing}
          exclusive
          onChange={handleLineSpacingChange}
          aria-labelledby="a11y-line-spacing-label"
          size="small"
          fullWidth
        >
          {LINE_SPACING_OPTIONS.map((v) => (
            <ToggleButton key={v} value={v} data-testid={`line-spacing-${v}`}>
              {v}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ---- Visual Section ---- */}
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        <Trans>Visual</Trans>
      </Typography>

      <Box sx={{ mb: 3 }}>
        <HighContrastToggle />
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="a11y-cvd-mode-label">
            <Trans>Color Vision Mode</Trans>
          </InputLabel>
          <Select
            labelId="a11y-cvd-mode-label"
            value={cvdMode}
            onChange={handleCvdModeChange}
            label={t`Color Vision Mode`}
            data-testid="a11y-cvd-mode-select"
          >
            {CVD_MODE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ---- Motion Section ---- */}
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        <Trans>Motion</Trans>
      </Typography>

      <Box sx={{ mb: 3 }}>
        <AnimationControl />
      </Box>

      {/* ---- Reset ---- */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={resetSettings}
          data-testid="a11y-reset-btn"
        >
          <Trans>Reset to Defaults</Trans>
        </Button>
      </Box>
    </Paper>
  );
};

export default AccessibilityPanel;
